import { useState, useCallback } from "react";
import stepsStyles from "../steps.module.css";
import { FieldError, SectionLabel } from "../../../FieldHelpers";
import { REGEX_PLANO_Z } from "../../../../services/validationRules";
import apiFetch from "../../../../../../shared/services/apiClient";
import { useEsCliente } from "../../../../../../shared/hooks/useTenantTipo";

const FIELD_LABELS = {
  idFilial: "ID de Filial",
  numPlano: "Número de Plano",
  numFolio: "Número de Folio",
};

// ── Hook: verifica si un campo ya existe en el condominio ──────────────────
function useUniquenessCheck() {
  const [checking, setChecking] = useState({});
  const [dupErrors, setDupErrors] = useState({});

  const check = useCallback(async (field, value, excluir) => {
    if (!value || value.trim() === "") return;
    setChecking((prev) => ({ ...prev, [field]: true }));
    try {
      const extra = excluir ? `&excluir=${excluir}` : "";
      const res = await apiFetch(
        `/api/residents/filial-check/?${field}=${encodeURIComponent(value.trim())}${extra}`,
      );
      const data = await res.json();
      setDupErrors((prev) => ({
        ...prev,
        [field]: data[field]
          ? `Este ${FIELD_LABELS[field]} ya está registrado en el condominio.`
          : null,
      }));
    } catch (_) {
      // Si falla el check no bloquear — el backend lo rechazará al guardar
    } finally {
      setChecking((prev) => ({ ...prev, [field]: false }));
    }
  }, []);

  return {
    check,
    checking,
    dupErrors,
    clearDup: (field) => setDupErrors((prev) => ({ ...prev, [field]: null })),
  };
}

export default function Filial({ formData, setFormData, errors = {} }) {
  const esCliente = useEsCliente();
  const { check: checkBase, checking, dupErrors, clearDup } = useUniquenessCheck();
  // Al editar, la filial actual no cuenta como duplicado de sí misma.
  const check = (field, value) => checkBase(field, value, formData.filial?.id);
  const [autoGenerado, setAutoGenerado] = useState(
    REGEX_PLANO_Z.test((formData.filial?.numPlano || "").trim())
  );
  const [generando, setGenerando] = useState(false);

  function handleChange(field, value) {
    setFormData((prev) => ({
      ...prev,
      filial: { ...prev.filial, [field]: value },
    }));
    clearDup(field);
  }

  // Plano y folio son texto libre: hay planos y folios reales que no siguen
  // el formato típico, así que no se fuerza ninguna máscara.
  function handlePlanoChange(e) {
    // Si el código es automático (Z-) el campo es read-only; ignorar cambios manuales
    if (autoGenerado) return;
    handleChange("numPlano", e.target.value);
  }

  async function generarCodigoAuto() {
    setGenerando(true);
    try {
      const res = await apiFetch("/api/residents/filial/siguiente-sin-plano/");
      const data = await res.json();
      handleChange("numPlano", data.numPlano);
      setAutoGenerado(true);
      clearDup("numPlano");
    } catch (_) {
      // Si falla, el usuario puede intentar de nuevo
    } finally {
      setGenerando(false);
    }
  }

  function limpiarPlano() {
    handleChange("numPlano", "");
    setAutoGenerado(false);
    clearDup("numPlano");
  }

  function handleFolioChange(e) {
    handleChange("numFolio", e.target.value);
  }

  const planoOk = (formData.filial.numPlano || "").trim() !== "";
  const folioOk = (formData.filial.numFolio || "").trim() !== "";

  // Combinar error de validación + error de duplicado por campo
  const err = (field) => errors[field] || dupErrors[field];

  const CheckSpinner = ({ field }) =>
    checking[field] ? (
      <i
        className="fa-solid fa-circle-notch fa-spin"
        style={{
          position: "absolute",
          right: 10,
          top: "50%",
          transform: "translateY(-50%)",
          color: "#60a5fa",
          fontSize: "0.75rem",
        }}
      />
    ) : null;

  const OkIcon = ({ field, formatOk }) =>
    formatOk &&
    formData.filial[field] &&
    !dupErrors[field] &&
    !checking[field] ? (
      <i
        className="fa-solid fa-circle-check"
        style={{
          position: "absolute",
          right: 10,
          top: "50%",
          transform: "translateY(-50%)",
          color: "#4ade80",
          fontSize: "0.75rem",
        }}
      />
    ) : null;

  // ── Modo cliente: solo el ID/código del cliente ───────────────────────────
  if (esCliente) {
    return (
      <div className="step">
        <h3 className="p-0 mt-2 mb-0 title" style={{ fontSize: "1.3rem" }}>Identificación del Cliente</h3>
        <p className={`${stepsStyles["inactive"]} mb-3`} style={{ fontSize: "1rem" }}>
          Asigná un código o identificador único para este cliente.
        </p>

        <div className={stepsStyles["form-wrapper"]}>
          <SectionLabel icon="fa-id-card">Identificación</SectionLabel>
          <div className="form-group">
            <label className="form-label">
              ID / Código de Cliente <span style={{ color: "#f87171" }}>*</span>
            </label>
            <div style={{ position: "relative" }}>
              <input
                name="idFilial"
                type="text"
                className={`${stepsStyles["form-control"]} form-control`}
                style={{ borderColor: err("idFilial") ? "#ef4444" : undefined }}
                placeholder="Ej: CLI-001"
                value={formData.filial.idFilial}
                onChange={(e) => handleChange("idFilial", e.target.value)}
                onBlur={() => check("idFilial", formData.filial.idFilial)}
              />
              <CheckSpinner field="idFilial" />
              {formData.filial.idFilial && !err("idFilial") && !checking.idFilial && (
                <i
                  className="fa-solid fa-circle-check"
                  style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#4ade80", fontSize: "0.75rem" }}
                />
              )}
            </div>
            <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 3 }}>
              Identificador único del cliente dentro de tu organización.
            </div>
            <FieldError msg={err("idFilial")} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="step">
      <h3 className="p-0 mt-2 mb-0 title" style={{fontSize:"1.3rem"}}>Información de la Filial</h3>
      <p className={`${stepsStyles["inactive"]} mb-3` } style={{fontSize:"1rem"}}>Inserte los siguientes detalles para continuar.</p>

      <div className={stepsStyles["form-wrapper"]}>
        <SectionLabel icon="fa-house">Identificación</SectionLabel>

        {/* ── ID Filial — OBLIGATORIO ────────────────────────────────── */}
        <div className="form-group">
          <label className="form-label">
            ID Filial <span style={{ color: "#f87171" }}>*</span>
          </label>
          <div style={{ position: "relative" }}>
            <input
              name="idFilial"
              type="text"
              className={`${stepsStyles["form-control"]} form-control`}
              style={{ borderColor: err("idFilial") ? "#ef4444" : undefined }}
              placeholder="Ejemplo: A01"
              value={formData.filial.idFilial}
              onChange={(e) => handleChange("idFilial", e.target.value)}
              onBlur={() => check("idFilial", formData.filial.idFilial)}
            />
            <CheckSpinner field="idFilial" />
            {formData.filial.idFilial &&
              !err("idFilial") &&
              !checking.idFilial && (
                <i
                  className="fa-solid fa-circle-check"
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#4ade80",
                    fontSize: "0.75rem",
                  }}
                />
              )}
          </div>
          <FieldError msg={err("idFilial")} />
        </div>

        <SectionLabel icon="fa-map">Registro catastral</SectionLabel>

        {/* ── Número Plano y Folio ───────────────────────────────────── */}
        <div className="form-group row g-3 g-md-4">
          <div className="col-12 col-md-6">
            <div style={{ display: "flex", alignItems: "center", minHeight: 28 }}>
              <label className="form-label m-0">
                Número Plano{" "}
                <span style={{ color: "var(--text-muted)", fontWeight: 400, fontSize: "0.8rem" }}>
                  (opcional)
                </span>
              </label>
            </div>

            <div style={{ position: "relative", marginTop: 6 }}>
              <input
                name="numPlano"
                type="text"
                className={`${stepsStyles["form-control"]} form-control`}
                style={{
                  borderColor: err("numPlano")
                    ? "#ef4444"
                    : planoOk && !dupErrors.numPlano && formData.filial.numPlano
                      ? "#4ade80"
                      : undefined,
                  paddingRight: autoGenerado ? 60 : undefined,
                  background: autoGenerado ? "var(--bg)" : undefined,
                  color: autoGenerado ? "var(--text-muted)" : undefined,
                  cursor: autoGenerado ? "default" : undefined,
                }}
                value={formData.filial.numPlano}
                onChange={handlePlanoChange}
                onBlur={() => {
                  if (planoOk && !autoGenerado) check("numPlano", formData.filial.numPlano);
                }}
                placeholder="Ej: A-10101-2020"
                maxLength={150}
                readOnly={autoGenerado}
              />
              {/* Ícono de candado cuando es auto-generado */}
              {autoGenerado && (
                <i
                  className="fa-solid fa-lock"
                  style={{
                    position: "absolute",
                    right: 28,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#a78bfa",
                    fontSize: "0.7rem",
                  }}
                />
              )}
              <CheckSpinner field="numPlano" />
              <OkIcon field="numPlano" formatOk={planoOk} />
            </div>
            <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 3 }}>
              {autoGenerado
                ? "Código automático generado - sin número de plano catastral"
                : "Tal como aparece en el plano. Se acepta cualquier formato."}
            </div>
            <FieldError msg={err("numPlano")} />
            <div style={{ marginTop: 6 }}>
              {!autoGenerado ? (
                <button
                  type="button"
                  onClick={generarCodigoAuto}
                  disabled={generando}
                  title="Generar código automático (sin número de plano catastral)"
                  style={{
                    fontSize: "0.7rem",
                    padding: "2px 9px",
                    borderRadius: 999,
                    border: "1px solid var(--border-card)",
                    background: "var(--bg-card)",
                    color: "var(--text-secondary)",
                    cursor: generando ? "not-allowed" : "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    whiteSpace: "nowrap",
                    opacity: generando ? 0.7 : 1,
                  }}
                >
                  {generando ? (
                    <i className="fa-solid fa-circle-notch fa-spin" style={{ fontSize: "0.68rem" }} />
                  ) : (
                    <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize: "0.68rem", color: "#a78bfa" }} />
                  )}
                  Generar automático
                </button>
              ) : (
                <button
                  type="button"
                  onClick={limpiarPlano}
                  style={{
                    fontSize: "0.7rem",
                    padding: "2px 9px",
                    borderRadius: 999,
                    border: "1px solid #f87171",
                    background: "transparent",
                    color: "#f87171",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    whiteSpace: "nowrap",
                  }}
                >
                  <i className="fa-solid fa-xmark" style={{ fontSize: "0.68rem" }} />
                  Limpiar
                </button>
              )}
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div style={{ display: "flex", alignItems: "center", minHeight: 28 }}>
              <label className="form-label m-0">
                Número Folio <span style={{ color: "#f87171" }}>*</span>
              </label>
            </div>
            <div style={{ position: "relative", marginTop: 6 }}>
              <input
                name="numFolio"
                type="text"
                className={`${stepsStyles["form-control"]} form-control`}
                style={{
                  borderColor: err("numFolio")
                    ? "#ef4444"
                    : folioOk && !dupErrors.numFolio
                      ? "#4ade80"
                      : undefined,
                }}
                value={formData.filial.numFolio}
                onChange={handleFolioChange}
                onBlur={() => {
                  if (folioOk) check("numFolio", formData.filial.numFolio);
                }}
                placeholder="Ej: 1-10101-F-000"
                maxLength={150}
              />
              <CheckSpinner field="numFolio" />
              <OkIcon field="numFolio" formatOk={folioOk} />
            </div>
            <div
              style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 3 }}
            >
              Tal como aparece en el folio real. Se acepta cualquier formato.
            </div>
            <FieldError msg={err("numFolio")} />
          </div>
        </div>

        {/* ── Tipo de Propiedad ──────────────────────────────────────── */}
        <div className="form-group">
          <label className="form-label">
            Tipo de Propiedad <span style={{ color: "#f87171" }}>*</span>
          </label>
          <select
            name="tipoPropiedad"
            className={`${stepsStyles["form-select"]} form-select ${stepsStyles["form-control"]} form-control`}
            style={{
              borderColor: errors.tipoPropiedad ? "#ef4444" : undefined,
            }}
            value={formData.filial.tipoPropiedad}
            onChange={(e) => handleChange("tipoPropiedad", e.target.value)}
          >
            <option value="">- Seleccioná el tipo -</option>
            <option value="Construida">Construida</option>
            <option value="Lote Vacio">Lote vacío</option>
            <option value="En Construccion">En construcción</option>
          </select>
          <FieldError msg={errors.tipoPropiedad} />
        </div>
      </div>
    </div>
  );
}
